import {
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
} from '@chakra-ui/react';
import { useGlobalContext } from 'GlobalContext';
import { useEffect, useState } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { toast } from 'react-toastify';
import { changeContractPaymentMethod } from 'services/contract';
import { Mixpanel } from 'services/mixpanel';
import { Contract } from 'shared/models/contract';
import { useFinancialContext } from '../../FinancialContext';

interface ChangePaymentMethodModalProps {
  isOpen: boolean;
  onClose(): void;
  contract: Contract;
}

const ChangePaymentMethodModal: React.FC<ChangePaymentMethodModalProps> = ({
  isOpen,
  onClose: onCloseFromProps,
  contract,
}) => {
  const { confirm } = useGlobalContext();
  const { reloadContracts } = useFinancialContext();

  const [loading, setLoading] = useState(false);

  const [method, setMethod] = useState<1 | 2>(contract.bankId!);

  const onClose = () => {
    onCloseFromProps();
    setLoading(false);
    setMethod(contract.bankId!);
  };

  useEffect(() => {
    if (isOpen && contract) {
      setMethod(contract.bankId);
    }
  }, [isOpen, contract]);

  const submit = () => {
    if (method === undefined) {
      return toast.error(
        'Página cacheada, por favor recarregue com F5 ou Ctrl + R',
        { autoClose: 5000 },
      );
    }

    confirm(
      `Alterar para  ${method === 1 ? 'Cartão de Crédito' : 'Boleto Bancário'}`,
      'Deseja continuar?',
      async () => {
        Mixpanel.track(
          'customers-financial-payment-method-submitted-new-payment-method',
        );
        setLoading(true);

        try {
          await changeContractPaymentMethod(contract.id!, method);
          await reloadContracts(false, contract);

          toast.success('Método de pagamento alterado');
          onClose();
        } catch (e: any) {
          if (e?.response?.status === 422)
            return toast.error(
              'Forma de pagamento já foi alterada para esse contrato dentro dos últimos 30 dias',
              { autoClose: 5000 },
            );

          toast.error('Falha ao alterar forma de pagamento');
        } finally {
          setLoading(false);
        }
      },
    );
  };

  return (
    <Modal
      closeOnOverlayClick={!loading}
      closeOnEsc={!loading}
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />

      <ModalContent>
        <ModalCloseButton isDisabled={loading} />
        <ModalHeader>Alterar Método de Pagamento</ModalHeader>

        <ModalBody>
          <Card bg="yellow.100">
            <CardBody>
              <HStack gap="1rem">
                <Icon as={MdInfoOutline} color="yellow.500" />
                <Text fontSize="0.9rem" fontWeight="400" color="gray.600">
                  Só é possível mudar o método de pagamento de um cliente com um
                  intervalo de 30 dias desde a última alteração.
                </Text>
              </HStack>
            </CardBody>
          </Card>

          <FormControl mt="1rem">
            <FormLabel fontSize="0.8rem" color="gray.500"></FormLabel>
            <Select
              id="select-payment-method"
              value={method}
              onChange={(e) => setMethod(Number(e.target.value) as 1 | 2)}
            >
              <option value="1">Cartão de Crédito</option>
              <option value="2">Boleto Bancário</option>
            </Select>
          </FormControl>
        </ModalBody>
        <ModalFooter>
          <Button
            isLoading={loading}
            onClick={submit}
            isDisabled={method === contract?.bankId}
            colorScheme="yooga"
            w="100%"
          >
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ChangePaymentMethodModal;
